// /top-movies — create (or edit) your all-time Top 5 / Top 10.
//
// Works without an account: a guest can search, pick, rank, preview and
// download/share the poster image. The draft lives in localStorage, so a
// refresh, a network error or the sign-up detour never loses it. When a guest
// chooses "Create a Free Account", the draft is flagged pendingSave and this
// page is the post-onboarding return path; once signed in, the ranking is
// saved to the new account (private until they choose to share it).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { TopMoviesService, getTopMoviesErrorMessage } from '../../services/topMoviesService';
import {
    addMovie, clearDraft, discardSetAside, draftFromSaved, emptyDraft, isComplete, loadDraft,
    matchesSaved, moveMovie, overflowFor, removeMovie, saveDraft, setRanked, setSize, TOP_MOVIES_SIZES,
} from '../../utils/topMoviesDraft';
import { setReferral } from '../../attribution/referral';
import { targetIndexFor } from '@aluvo/constants/src/topMovies';
import { ANALYTICS_EVENTS } from '../../analytics';
import { trackTopMovies } from '../../analytics/topMoviesAnalytics';
import { useShareMetadata } from '../../utils/shareMetadata';
import { PosterImage } from '../../components/TopMovies/TopMoviesGrid';
import TopMoviesSharePanel from '../../components/TopMovies/TopMoviesSharePanel';
import RankSlots from '../../components/RankSlots/RankSlots';
import styles from './TopMovies.module.css';

export { targetIndexFor };

const SEARCH_DEBOUNCE_MS = 350;
const RESUME_INTENT_MS = 2 * 60 * 60 * 1000;
const RESUME_PATH = '/top-movies?resume=1';

/** Return here after sign-up + onboarding (read by onboardingExitPath / Login `from`). */
function rememberReturnToTopMovies() {
    try {
        sessionStorage.setItem('aluvo_pending_intent', JSON.stringify({
            type: 'TOP_MOVIES',
            returnUrl: RESUME_PATH,
            expiresAt: Date.now() + RESUME_INTENT_MS,
        }));
    } catch { /* storage blocked: the draft still survives in localStorage */ }
}

function SearchResults({ results, draft, onAdd }) {
    const full = draft.picks.length >= draft.size;
    return (
        <ul className={styles.results} aria-label="Search results">
            {results.map((movie) => {
                const picked = draft.picks.some((m) => m.tmdbId === movie.tmdbId);
                return (
                    <li key={movie.tmdbId}>
                        <button
                            type="button"
                            className={`${styles.result} ${picked ? styles.resultPicked : ''}`}
                            onClick={() => onAdd(movie)}
                            disabled={picked || full}
                            aria-label={picked ? `${movie.title} is already on your list` : `Add ${movie.title}${movie.year ? ` (${movie.year})` : ''}`}
                        >
                            <PosterImage movie={movie} className={styles.resultPoster} />
                            <span className={styles.resultText}>
                                <strong>{movie.title}</strong>
                                <span>{movie.year || '—'}</span>
                            </span>
                            <span className={styles.resultAction} aria-hidden="true">{picked ? '✓' : '+'}</span>
                        </button>
                    </li>
                );
            })}
        </ul>
    );
}

export default function TopMovies() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const ref = params.get('ref');
    const loggedIn = Boolean(user);

    const [draft, setDraft] = useState(() => loadDraft() || emptyDraft());
    const [saved, setSaved] = useState(null);
    const [ready, setReady] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [replacePrompt, setReplacePrompt] = useState(false);
    const [justSavedNewAccount, setJustSavedNewAccount] = useState(false);

    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [notice, setNotice] = useState('');
    const searchTicket = useRef(0);

    const [pendingSize, setPendingSize] = useState(null);
    const [publicLink, setPublicLink] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    useShareMetadata(useMemo(() => ({
        title: 'Make Your All-Time Top 5 Movies | Aluvo',
        description: 'Every movie fan has favorites. Pick your all-time Top 5 or Top 10, rank them and share a poster graphic — free.',
        url: `${window.location.origin}/top-movies`,
    }), []));

    // Arriving from someone's shared list: remember it for sign-up attribution.
    useEffect(() => {
        if (ref) setReferral('top_movies', ref);
        trackTopMovies(ANALYTICS_EVENTS.TOP_MOVIES_OPENED, { logged_in: loggedIn, ...(ref ? { ref } : {}) }, { onceKey: 'page' });
    }, [ref, loggedIn]);

    const save = useCallback(async (nextDraft, { visibility, source } = {}) => {
        setSaving(true);
        setSaveError('');
        try {
            const data = await TopMoviesService.save({
                size: nextDraft.size,
                ranked: nextDraft.ranked,
                movies: nextDraft.picks.map((m) => m.tmdbId),
                visibility,
                ref: ref || undefined,
            });
            const result = data.topMovies;
            setSaved(result);
            setDraft(draftFromSaved(result));
            setPublicLink(result.visibility === 'public');
            clearDraft();
            trackTopMovies(ANALYTICS_EVENTS.TOP_MOVIES_SAVED, { size: result.size, ranked: result.ranked, logged_in: true, source: source || 'creator' }, { listSlug: result.shareSlug });
            return result;
        } catch (err) {
            setSaveError(getTopMoviesErrorMessage(err, "Couldn't save your list. Your picks are kept on this device — try again."));
            return null;
        } finally {
            setSaving(false);
        }
    }, [ref]);

    // Load the saved list once auth is known, and finish a guest → sign-up handoff.
    useEffect(() => {
        if (authLoading) return undefined;
        let cancelled = false;
        if (!loggedIn) {
            setSaved(null);
            setReady(true);
            return undefined;
        }
        (async () => {
            try {
                const data = await TopMoviesService.getMine();
                if (cancelled) return;
                const existing = data.topMovies;
                const local = loadDraft();
                setSaved(existing);
                if (local?.pendingSave && isComplete(local)) {
                    if (!existing) {
                        // New account from the guest flow: attach the ranking they made. Private
                        // until they choose to share it.
                        setDraft(local);
                        const result = await save(local, { visibility: 'private', source: 'signup_handoff' });
                        if (!cancelled && result) setJustSavedNewAccount(true);
                    } else if (!matchesSaved(local, existing)) {
                        setDraft(local);
                        setReplacePrompt(true);
                    } else {
                        clearDraft();
                        setDraft(draftFromSaved(existing));
                    }
                } else if (existing) {
                    setDraft(draftFromSaved(existing));
                    setPublicLink(existing.visibility === 'public');
                } else if (local) {
                    setDraft(local);
                }
            } catch (err) {
                if (!cancelled) setLoadError(getTopMoviesErrorMessage(err, "Couldn't load your saved list."));
            } finally {
                if (!cancelled) setReady(true);
            }
        })();
        return () => { cancelled = true; };
    }, [authLoading, loggedIn, save]);

    // Keep the work-in-progress on this device. A list that matches what's saved needs no draft.
    useEffect(() => {
        if (!ready) return;
        if (saved && matchesSaved(draft, saved) && !draft.setAside.length) clearDraft();
        else if (draft.picks.length || draft.setAside.length) saveDraft(draft);
    }, [draft, saved, ready]);

    // Fire "completed" when a list fills up (once per size per page load).
    const complete = isComplete(draft);
    useEffect(() => {
        if (complete) trackTopMovies(ANALYTICS_EVENTS.TOP_MOVIES_COMPLETED, { size: draft.size, ranked: draft.ranked, logged_in: loggedIn, ...(ref ? { ref } : {}) }, { onceKey: `complete-${draft.size}` });
    }, [complete, draft.size, draft.ranked, loggedIn, ref]);

    // Debounced movie-only search; stale responses are dropped.
    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) {
            setResults([]);
            setSearching(false);
            setSearchError('');
            return undefined;
        }
        setSearching(true);
        const ticket = ++searchTicket.current;
        const timer = setTimeout(async () => {
            try {
                const data = await TopMoviesService.searchMovies(q);
                if (ticket !== searchTicket.current) return;
                setResults((data.results || []).filter((m) => m.tmdbId).slice(0, 12));
                setSearchError('');
            } catch (err) {
                if (ticket !== searchTicket.current) return;
                setSearchError(getTopMoviesErrorMessage(err, "Search isn't working right now. Please try again."));
            } finally {
                if (ticket === searchTicket.current) setSearching(false);
            }
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [query]);

    function handleAdd(movie) {
        const { draft: next, status } = addMovie(draft, movie);
        if (status === 'duplicate') return setNotice(`${movie.title} is already on your list.`);
        if (status === 'full') return setNotice(draft.size === 5 ? 'Your Top 5 is full. Remove one, or expand to a Top 10.' : 'Your Top 10 is full. Remove one to make room.');
        if (!draft.picks.length) trackTopMovies(ANALYTICS_EVENTS.TOP_MOVIES_CREATION_STARTED, { size: draft.size, logged_in: loggedIn, ...(ref ? { ref } : {}) }, { onceKey: 'start' });
        trackTopMovies(ANALYTICS_EVENTS.TOP_MOVIES_MOVIE_ADDED, { size: draft.size, movie_count: next.picks.length, logged_in: loggedIn });
        setNotice(status === 'restored' ? `${movie.title} is back on your list.` : `Added ${movie.title} at #${next.picks.length}.`);
        setDraft(next);
        return undefined;
    }

    function requestSize(size) {
        if (size === draft.size) return;
        if (overflowFor(draft, size) > 0) setPendingSize(size);
        else setDraft(setSize(draft, size));
    }

    function confirmSize() {
        setDraft(setSize(draft, pendingSize));
        setPendingSize(null);
    }

    function startSignup() {
        saveDraft({ ...draft, pendingSave: true });
        rememberReturnToTopMovies();
        trackTopMovies(ANALYTICS_EVENTS.TOP_MOVIES_SIGNUP_STARTED, { size: draft.size, ranked: draft.ranked, ...(ref ? { ref } : {}) }, { onceKey: 'signup' });
        navigate('/register', { state: { from: { pathname: '/top-movies', search: '?resume=1' } } });
    }

    function startLogin() {
        saveDraft({ ...draft, pendingSave: true });
        rememberReturnToTopMovies();
        navigate('/login', { state: { from: { pathname: '/top-movies', search: '?resume=1' } } });
    }

    async function handleDelete() {
        // eslint-disable-next-line no-alert
        if (!window.confirm('Delete your Top Movies? Your share link will stop working.')) return;
        try {
            await TopMoviesService.remove();
            setSaved(null);
            setDraft(emptyDraft());
            clearDraft();
        } catch (err) {
            setSaveError(getTopMoviesErrorMessage(err, "Couldn't delete your list."));
        }
    }

    const dirty = !saved || !matchesSaved(draft, saved);
    const visibilityChanged = Boolean(saved) && (saved.visibility === 'public') !== publicLink;
    const sharedForPanel = saved && !dirty && saved.visibility === 'public' ? { slug: saved.shareSlug, shareUrl: saved.shareUrl } : null;
    const progressPct = Math.round((draft.picks.length / draft.size) * 100);

    return (
        <main className={styles.page}>
            <div className={styles.glow} aria-hidden="true" />
            <div className={styles.shell}>
                <header className={styles.hero}>
                    <span className={styles.eyebrow}>{saved ? 'My Top Movies' : 'Aluvo · Top Movies'}</span>
                    <h1>Your All-Time Favorite Movies <span aria-hidden="true">🎬</span></h1>
                    <p>Every movie fan has favorites. Which ones make your list?</p>
                    <p className={styles.muted}>
                        Want to rank TV shows, anime or a genre? <Link to="/rankings/new" state={{ source: 'top_movies' }}>Make a custom ranking →</Link>
                        {loggedIn && <>{' · '}<Link to="/me/rankings">See my rankings →</Link></>}
                    </p>
                </header>

                {loadError && <p className={styles.errorBanner} role="alert">{loadError}</p>}

                {justSavedNewAccount && (
                    <section className={styles.welcome} aria-live="polite">
                        <strong>Welcome to Aluvo — your Top {draft.size} is saved to your profile. ✓</strong>
                        <p>It&apos;s private until you share it. While you&apos;re here:</p>
                        <ul>
                            <li><Link to="/discover">🎯 Take a movie challenge</Link> — prove you&apos;ve seen it</li>
                            <li><Link to="/how-it-works">🪙 Earn collectible coins</Link> for every title you verify</li>
                            <li><Link to="/collections">🏆 Complete collections</Link> of directors and franchises</li>
                            <li><Link to="/me">📋 Build your Watchlist</Link> of what&apos;s next</li>
                        </ul>
                    </section>
                )}

                {replacePrompt && (
                    <section className={styles.prompt} role="alertdialog" aria-labelledby="replace-title">
                        <strong id="replace-title">You already have a saved Top {saved?.size}.</strong>
                        <p>Replace it with the list you just made?</p>
                        <div className={styles.promptActions}>
                            <button type="button" className={styles.primary} onClick={async () => { setReplacePrompt(false); await save(draft, { source: 'signup_handoff' }); }}>Replace saved list</button>
                            <button type="button" className={styles.secondary} onClick={() => { setReplacePrompt(false); clearDraft(); setDraft(draftFromSaved(saved)); }}>Keep my saved list</button>
                        </div>
                    </section>
                )}

                <section className={styles.controls} aria-label="List options">
                    <div className={styles.segmented} role="group" aria-label="List size">
                        {TOP_MOVIES_SIZES.map((size) => (
                            <button key={size} type="button" aria-pressed={draft.size === size} onClick={() => requestSize(size)}>Top {size}</button>
                        ))}
                    </div>
                    <div className={styles.segmented} role="group" aria-label="Presentation">
                        <button type="button" aria-pressed={draft.ranked} onClick={() => setDraft(setRanked(draft, true))}>Ranked</button>
                        <button type="button" aria-pressed={!draft.ranked} onClick={() => setDraft(setRanked(draft, false))}>Unranked</button>
                    </div>
                    <div className={styles.progress} aria-live="polite">
                        <span><strong>{draft.picks.length} of {draft.size}</strong> selected</span>
                        <span className={styles.track} aria-hidden="true"><span style={{ width: `${progressPct}%` }} /></span>
                    </div>
                </section>

                {pendingSize && (
                    <section className={styles.prompt} role="alertdialog" aria-labelledby="size-title">
                        <strong id="size-title">Switch to a Top {pendingSize}?</strong>
                        <p>
                            Your picks #{pendingSize + 1}–#{draft.picks.length} will be set aside, not deleted.
                            Switch back to Top {draft.size} any time to bring them back.
                        </p>
                        <div className={styles.promptActions}>
                            <button type="button" className={styles.primary} onClick={confirmSize}>Switch to Top {pendingSize}</button>
                            <button type="button" className={styles.secondary} onClick={() => setPendingSize(null)}>Cancel</button>
                        </div>
                    </section>
                )}

                <div className={styles.workspace}>
                    <section className={styles.searchCol} aria-label="Find movies">
                        <label className={styles.searchLabel} htmlFor="top-movies-search">Search movies</label>
                        <input
                            id="top-movies-search"
                            className={styles.searchInput}
                            type="search"
                            placeholder="e.g. The Godfather"
                            autoComplete="off"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                        <p className={styles.notice} aria-live="polite">{notice}</p>
                        {searching && <p className={styles.muted} role="status">Searching…</p>}
                        {searchError && <p className={styles.errorText} role="alert">{searchError}</p>}
                        {!searching && !searchError && query.trim().length >= 2 && results.length === 0 && <p className={styles.muted}>No movies found. Try another title.</p>}
                        {results.length > 0 && <SearchResults results={results} draft={draft} onAdd={handleAdd} />}
                        {draft.size === 5 && complete && (
                            <button type="button" className={styles.ghost} onClick={() => requestSize(10)}>Want more? Expand to a Top 10 →</button>
                        )}
                    </section>

                    <section className={styles.listCol} aria-label={`Your Top ${draft.size}`}>
                        <div className={styles.listHead}>
                            <h2>{draft.ranked ? `Your Top ${draft.size}` : `Your ${draft.size} favorites`}</h2>
                            <span className={styles.muted}>{draft.ranked ? 'Drag ⋮⋮ or use the arrows to rank' : 'Drag ⋮⋮ to arrange · shown without numbers'}</span>
                        </div>
                        <RankSlots
                            items={draft.picks}
                            size={draft.size}
                            ranked={draft.ranked}
                            label={`Your Top ${draft.size}`}
                            onRemove={(movie) => setDraft(removeMovie(draft, movie.tmdbId))}
                            onMove={(from, to) => setDraft(moveMovie(draft, from, to))}
                        />
                        {draft.setAside.length > 0 && (
                            <div className={styles.setAside}>
                                <p>
                                    <strong>Set aside ({draft.setAside.length}):</strong>{' '}
                                    {draft.setAside.map((m) => m.title).join(', ')}
                                </p>
                                <div className={styles.promptActions}>
                                    {draft.size === 5 && <button type="button" className={styles.ghost} onClick={() => requestSize(10)}>Bring back (Top 10)</button>}
                                    <button type="button" className={styles.ghost} onClick={() => setDraft(discardSetAside(draft))}>Discard</button>
                                </div>
                            </div>
                        )}
                    </section>
                </div>

                {complete ? (
                    <>
                        {loggedIn && ready && (
                            <section className={styles.saveBar} aria-label="Save">
                                <label className={styles.toggle}>
                                    <input type="checkbox" checked={publicLink} onChange={(e) => setPublicLink(e.target.checked)} />
                                    <span>
                                        <strong>Public link</strong>
                                        <span>Anyone with the link can see your list, and it appears on your profile.</span>
                                    </span>
                                </label>
                                <div className={styles.promptActions}>
                                    <button
                                        type="button"
                                        className={styles.primary}
                                        onClick={() => save(draft, { visibility: publicLink ? 'public' : 'private', source: saved ? 'edit' : 'create' })}
                                        disabled={saving || (!dirty && !visibilityChanged)}
                                    >
                                        {saving ? 'Saving…' : saved ? (dirty || visibilityChanged ? 'Save changes' : 'Saved ✓') : 'Save to my profile'}
                                    </button>
                                    {saved?.shareUrl && !dirty && (
                                        <Link className={styles.secondary} to={`/top-movies/${saved.shareSlug}`}>View public page</Link>
                                    )}
                                </div>
                                {saveError && <p className={styles.errorText} role="alert">{saveError}</p>}
                            </section>
                        )}

                        <section className={styles.panel} aria-label="Share">
                            <h2 className={styles.panelTitle}>Share your Top {draft.size}</h2>
                            <TopMoviesSharePanel
                                movies={draft.picks}
                                size={draft.size}
                                ranked={draft.ranked}
                                shared={sharedForPanel}
                                loggedIn={loggedIn}
                            />
                        </section>

                        {!loggedIn && !authLoading && (
                            <section className={styles.signupCard} aria-label="Save your list">
                                <div>
                                    <h2>Love your list? Save it to your Aluvo profile.</h2>
                                    <p>Get a link anyone can open, edit it any time, and discover movie challenges and collectible coins.</p>
                                </div>
                                <div className={styles.promptActions}>
                                    <button type="button" className={styles.primary} onClick={startSignup}>Create a Free Account</button>
                                    <button type="button" className={styles.ghost} onClick={startLogin}>I have an account</button>
                                </div>
                            </section>
                        )}
                    </>
                ) : (
                    <p className={styles.muted} style={{ textAlign: 'center' }}>
                        Pick {draft.size - draft.picks.length} more {draft.size - draft.picks.length === 1 ? 'movie' : 'movies'} to create your shareable Top {draft.size}.
                    </p>
                )}

                {saved && (
                    <p className={styles.deleteRow}>
                        <button type="button" className={styles.linkButton} onClick={handleDelete}>Delete my Top Movies</button>
                    </p>
                )}
            </div>
        </main>
    );
}
